import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/news';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'horizontal';
}

function CategoryBadge({ article }: { article: Article }) {
  const isGuide = article.type === 'guide';
  const cls = isGuide
    ? 'bg-secondary-100 text-secondary-800'
    : 'bg-primary-100 text-primary-800';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap ${cls}`}
    >
      <i className={isGuide ? 'ri-book-2-line' : 'ri-newspaper-line'}></i>
      {article.category}
    </span>
  );
}

function MetaRow({ article }: { article: Article }) {
  return (
    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-foreground-500">
      <span className="inline-flex items-center gap-1">
        <i className="ri-calendar-line"></i>
        {article.date}
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="ri-eye-line"></i>
        {article.views.toLocaleString()}
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="ri-time-line"></i>
        {article.readTime}
      </span>
    </div>
  );
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group card-hover relative block overflow-hidden rounded-lg border border-background-200 bg-background-50"
      >
        <div className="relative w-full h-64 md:h-80 overflow-hidden">
          <img
            src={article.cover}
            alt={article.title}
            className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent"></div>
          <div className="absolute left-4 top-4">
            <CategoryBadge article={article} />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
            <h3 className="font-heading text-lg md:text-2xl font-bold text-white leading-snug line-clamp-2">
              {article.title}
            </h3>
            <p className="mt-2 text-sm text-white/85 line-clamp-2">{article.summary}</p>
            <div className="mt-3 flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-white/75">
              <span className="inline-flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group card-hover flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 hover:border-primary-300"
      >
        <div className="relative h-20 w-28 flex-shrink-0 overflow-hidden rounded-md">
          <img
            src={article.cover}
            alt={article.title}
            className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 transition-colors group-hover:text-primary-700">
            {article.title}
          </h3>
          <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views.toLocaleString()}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group card-hover flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 hover:border-primary-300"
    >
      <div className="relative w-full h-44 overflow-hidden">
        <img
          src={article.cover}
          alt={article.title}
          className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3">
          <CategoryBadge article={article} />
        </div>
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 transition-colors group-hover:text-primary-700">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 line-clamp-2 flex-1">{article.summary}</p>
        <div className="mt-4">
          <MetaRow article={article} />
        </div>
      </div>
    </Link>
  );
}